const GREEN = "#3F5A47";
const TERRA = "#B46A45";
const SAND = "#D6C2A6";

export const DriftingSeed = ({ x = 0, y = 0, rotate = 0, scale = 1, color = GREEN, className = "", delay = 0 }) => (
  <g transform={`translate(${x} ${y})`}>
    <g className={className} style={className ? { animationDelay: `${delay}s` } : undefined}>
      <g transform={`rotate(${rotate}) scale(${scale})`}>
        <line x1="0" y1="0" x2="0" y2="24" stroke={color} strokeWidth="1.1" strokeLinecap="round" />
        {[-64, -38, -13, 13, 38, 64].map((deg, i) => {
          const len = 12 + (i % 3) * 3;
          const rad = ((deg - 90) * Math.PI) / 180;
          return (
            <line
              key={deg}
              x1="0"
              y1="0"
              x2={Math.cos(rad) * len}
              y2={Math.sin(rad) * len}
              stroke={color}
              strokeWidth="0.85"
              strokeLinecap="round"
              opacity="0.9"
            />
          );
        })}
        <circle cx="0" cy="25.5" r="1.6" fill={color} />
      </g>
    </g>
  </g>
);

const SpokeHead = ({ cx, cy, count, rBase, rVar, color, skip = null }) => {
  const spokes = [];
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2 + Math.sin(i * 12.9898) * 0.06;
    if (skip && a > skip[0] && a < skip[1]) continue;
    const r = rBase + Math.abs(Math.sin(i * 7.13)) * rVar;
    const x2 = cx + Math.cos(a) * r;
    const y2 = cy + Math.sin(a) * r;
    const hx = cx + Math.cos(a) * 9;
    const hy = cy + Math.sin(a) * 9;
    const p1 = a + 0.3;
    const p2 = a - 0.3;
    spokes.push(
      <g key={i} opacity={0.55 + Math.abs(Math.sin(i * 3.7)) * 0.4}>
        <line x1={hx} y1={hy} x2={x2} y2={y2} stroke={color} strokeWidth="1" strokeLinecap="round" />
        <line x1={x2} y1={y2} x2={x2 + Math.cos(p1) * 12} y2={y2 + Math.sin(p1) * 12} stroke={color} strokeWidth="0.85" strokeLinecap="round" />
        <line x1={x2} y1={y2} x2={x2 + Math.cos(p2) * 12} y2={y2 + Math.sin(p2) * 12} stroke={color} strokeWidth="0.85" strokeLinecap="round" />
      </g>
    );
  }
  return (
    <g>
      <circle cx={cx} cy={cy} r="7" stroke={color} strokeWidth="1.2" fill="none" />
      {spokes}
    </g>
  );
};

export const DandelionFull = ({ className = "", color = GREEN, accent = TERRA }) => {
  const cx = 165;
  const cy = 185;
  return (
    <svg viewBox="0 0 420 560" className={className} fill="none" aria-hidden="true">
      <g className="dandelion-sway">
        <path
          d={`M ${cx} ${cy + 10} C ${cx - 26} ${cy + 130}, ${cx + 30} ${cy + 250}, ${cx - 10} 545`}
          stroke={color}
          strokeWidth="1.7"
          strokeLinecap="round"
        />
        <path
          d={`M ${cx - 2} 430 C 130 415, 112 390, 96 372 C 124 380, 150 402, ${cx - 2} 430 Z`}
          stroke={color}
          strokeWidth="1.2"
          strokeLinecap="round"
          opacity="0.8"
        />
        <SpokeHead cx={cx} cy={cy} count={42} rBase={84} rVar={30} color={color} />
      </g>
      <DriftingSeed x={318} y={120} rotate={18} color={accent} className="seed-drift-a" />
      <DriftingSeed x={352} y={205} rotate={-12} scale={0.85} color={color} className="seed-drift-b" delay={1.4} />
      <DriftingSeed x={300} y={268} rotate={26} scale={0.7} color={SAND} className="seed-drift-a" delay={2.6} />
      <DriftingSeed x={368} y={66} rotate={-24} scale={0.75} color={color} className="seed-drift-b" delay={0.8} />
    </svg>
  );
};

export const BudClosed = ({ className = "" }) => (
  <svg viewBox="0 0 200 280" className={className} fill="none" aria-hidden="true">
    <path d="M100 270 C 92 220, 108 182, 100 140" stroke={GREEN} strokeWidth="1.6" strokeLinecap="round" />
    <path d="M100 138 C 82 128, 76 92, 86 52 C 92 34, 108 34, 114 52 C 124 92, 118 128, 100 138 Z" stroke={GREEN} strokeWidth="1.4" strokeLinejoin="round" />
    <path d="M100 130 C 96 100, 98 70, 100 46" stroke={GREEN} strokeWidth="0.9" opacity="0.7" strokeLinecap="round" />
    <path d="M92 128 C 88 100, 90 74, 94 52" stroke={GREEN} strokeWidth="0.9" opacity="0.7" strokeLinecap="round" />
    <path d="M108 128 C 112 100, 110 74, 106 52" stroke={GREEN} strokeWidth="0.9" opacity="0.7" strokeLinecap="round" />
    <path d="M92 136 l -9 13 M108 136 l 9 13 M100 140 l 0 13" stroke={GREEN} strokeWidth="1.1" strokeLinecap="round" opacity="0.85" />
    <path d="M55 272 C 80 266, 122 278, 152 269" stroke={SAND} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const HeadOpen = ({ className = "" }) => (
  <svg viewBox="0 0 240 260" className={className} fill="none" aria-hidden="true">
    <path d="M110 118 C 102 160, 118 210, 106 252" stroke={GREEN} strokeWidth="1.5" strokeLinecap="round" />
    <SpokeHead cx={110} cy={110} count={34} rBase={64} rVar={22} color={GREEN} skip={[5.2, 6.1]} />
    <DriftingSeed x={172} y={42} rotate={20} scale={0.8} color={TERRA} className="seed-drift-a" />
    <DriftingSeed x={196} y={92} rotate={-14} scale={0.65} color={GREEN} className="seed-drift-b" delay={1.8} />
  </svg>
);

export const SeedCluster = ({ className = "" }) => (
  <svg viewBox="0 0 200 170" className={className} fill="none" aria-hidden="true">
    <DriftingSeed x={52} y={70} rotate={-18} color={GREEN} className="seed-drift-a" />
    <DriftingSeed x={102} y={46} rotate={8} scale={1.1} color={TERRA} className="seed-drift-b" delay={1.2} />
    <DriftingSeed x={150} y={84} rotate={24} scale={0.85} color={GREEN} className="seed-drift-a" delay={2.4} />
  </svg>
);

export const Sprout = ({ className = "" }) => (
  <svg viewBox="0 0 220 220" className={className} fill="none" aria-hidden="true">
    <path d="M15 150 C 55 142, 95 156, 135 148 C 160 143, 190 151, 205 147" stroke={SAND} strokeWidth="2.2" strokeLinecap="round" />
    <path d="M110 148 C 106 128, 114 112, 110 94" stroke={GREEN} strokeWidth="1.6" strokeLinecap="round" />
    <path d="M110 102 C 92 98, 78 86, 73 66 C 92 70, 106 84, 110 102 Z" stroke={GREEN} strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M110 94 C 126 90, 140 79, 145 60 C 128 64, 114 77, 110 94 Z" stroke={GREEN} strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M104 152 C 101 162, 99 169, 97 177 M112 152 C 114 163, 116 170, 119 178 M108 152 l 0 24" stroke={TERRA} strokeWidth="1" strokeLinecap="round" opacity="0.55" />
  </svg>
);

export const SeedOrnament = ({ className = "" }) => (
  <svg viewBox="0 0 72 42" className={className} fill="none" aria-hidden="true">
    <DriftingSeed x={36} y={6} rotate={0} scale={0.62} color={TERRA} />
    <DriftingSeed x={16} y={12} rotate={-26} scale={0.5} color={GREEN} />
    <DriftingSeed x={56} y={12} rotate={26} scale={0.5} color={GREEN} />
  </svg>
);
